<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useAppConfig } from '../../composables/use-app-config';
import { Product } from '@shared/types/domain';
import { getStaticHostUrl } from '../../core/static-host';

const props = defineProps<{
  product: Product;
}>();

const emit = defineEmits<{
  (e: 'select', product: Product): void;
  (e: 'quick-add', product: Product): void;
}>();

const isAvailable = computed(() => {
  const raw = (props.product as { is_available?: unknown }).is_available;
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return Boolean(raw);
});

// #980 (C4): same min-selection semantics as use-product-detail — the catalog
// may emit min_selection (menu_query_service) or min_required (menu_catalog_service).
const groupRequiresSelection = (group: unknown): boolean => {
  const candidate = (group || {}) as Record<string, unknown>;
  const raw = candidate.min_selection ?? candidate.min_required ?? candidate.min_selections;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) return true;
  return Boolean(candidate.is_required);
};

// A product needs the detail modal when ANY modifier group (own or inside a
// package component) requires at least one selection. Business rule: required
// modifiers/specs must be validated when adding to cart — no quick-add bypass.
const requiresSelection = computed(() => {
  const groups = (props.product.modifier_groups || []) as unknown[];
  if (groups.some(groupRequiresSelection)) return true;
  const components = (props.product.package_components || []) as Array<{
    modifier_groups?: unknown[];
  }>;
  return components.some((component) =>
    (component.modifier_groups || []).some(groupRequiresSelection),
  );
});

const appConfig = useAppConfig();
const formattedPrice = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: appConfig.config.currency_code || 'USD'
  }).format(props.product.price);
});

const serviceModeLabel = computed(() =>
  props.product.is_quick_serve ? 'Entrega inmediata' : 'Requiere preparación'
);

const serviceModeClass = computed(() =>
  props.product.is_quick_serve ? 'service-quick' : 'service-prep'
);

const offerLabel = computed(() => {
  const labels = Array.isArray(props.product.labels) ? props.product.labels : [];
  const promo = labels.find((label) => {
    const type = String(label?.label_type || '').toLowerCase();
    return type === 'promo' || type === 'discount' || type === 'offer';
  });
  if (promo?.name) return String(promo.name);

  const byName = labels.find((label) => {
    const name = String(label?.name || '').toLowerCase();
    return name.includes('oferta') || name.includes('promo') || name.includes('descuento');
  });
  if (byName?.name) return String(byName.name);

  if (props.product.has_active_promotion) return 'Oferta';
  return '';
});

const normalizeMenuAssetUrl = (url: string | null | undefined): string => {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const staticHost = getStaticHostUrl();

  // Already absolute URL - just normalize the path
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const parsed = new URL(raw);
      const normalizedPath = parsed.pathname
        .replace('/assets/cafeter-a-de-prueba/', '/assets/enfa/')
        .replace('/assets/cafeteria-de-prueba/', '/assets/enfa/');
      return `${parsed.origin}${normalizedPath}${parsed.search}${parsed.hash}`;
    } catch {
      return raw;
    }
  }

  // Relative path - prepend static host
  const normalizedPath = raw
    .replace('/assets/cafeter-a-de-prueba/', '/assets/enfa/')
    .replace('/assets/cafeteria-de-prueba/', '/assets/enfa/');

  return staticHost ? `${staticHost}${normalizedPath}` : normalizedPath;
};

const DEFAULT_SVG_PLACEHOLDER = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200" fill="%23f1f5f9"><rect width="300" height="200" fill="%23e2e8f0"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%2394a3b8">Sin imagen</text></svg>';

const imageSource = computed(() => {
  const raw = props.product.image_path || (props.product as any).image_url;
  if (raw) return normalizeMenuAssetUrl(raw);
  return DEFAULT_SVG_PLACEHOLDER;
});

const currentImageSource = ref(imageSource.value);

watch(
  () => imageSource.value,
  (next) => {
    currentImageSource.value = next;
  },
  { immediate: true }
);

const getPlaceholderImage = (): string => {
  return DEFAULT_SVG_PLACEHOLDER;
};

const handleImageError = (event: Event): void => {
  const target = event.target as HTMLImageElement | null;
  if (!target) return;

  if (currentImageSource.value !== DEFAULT_SVG_PLACEHOLDER) {
    currentImageSource.value = DEFAULT_SVG_PLACEHOLDER;
  }
};

const handlePrimaryAction = () => {
  if (!isAvailable.value) return;
  // #980 (C4): '+' must add directly to the cart when no required selection
  // blocks it. Products with required modifier groups / package components
  // keep going through the detail modal (validation must not be bypassed).
  if (requiresSelection.value) {
    emit('select', props.product);
    return;
  }
  emit('quick-add', props.product);
};

const quickAddAriaLabel = computed(() =>
  requiresSelection.value
    ? `Elegir opciones de ${props.product.name}`
    : `Agregar ${props.product.name} al carrito`,
);

const handleCardSelect = () => {
  if (!isAvailable.value) return;
  emit('select', props.product);
};
</script>

<template>
  <div
    class="product-card"
    :class="{ 'is-unavailable': !isAvailable }"
    @click="handleCardSelect"
  >
    <div class="product-card__image-container">
      <img 
        :src="currentImageSource" 
        :alt="product.name" 
        class="product-card__image"
        loading="lazy"
        @error="handleImageError"
      />
      <div v-if="offerLabel" class="product-card__badge promo">
        {{ offerLabel }}
      </div>
      <div v-if="!isAvailable" class="product-card__badge sold-out">
        Agotado
      </div>
      <button
        v-else
        class="product-card__quick-add"
        type="button"
        :aria-label="quickAddAriaLabel"
        @click.stop="handlePrimaryAction"
      >
        <span>+</span>
      </button>
      <div v-if="!isAvailable" class="product-card__overlay">
        <span>Producto agotado</span>
      </div>
    </div>

    <div class="product-card__content">
      <div class="product-card__header">
        <h3 class="product-card__title">{{ product.name }}</h3>
        <span class="product-card__price">{{ formattedPrice }}</span>
      </div>
      <p v-if="product.menu_subcategory_name" class="product-card__subcategory">
        {{ product.menu_subcategory_name }}
      </p>
      
      <p class="product-card__desc">{{ product.description }}</p>
      <div v-if="product.labels && product.labels.length" class="product-card__labels">
        <span v-for="label in product.labels" :key="label.id" class="product-card__label-chip">
          {{ label.name }}
        </span>
      </div>
      <div class="product-card__service-mode" :class="serviceModeClass">
        {{ serviceModeLabel }}
      </div>

      <div class="product-card__footer">
        <div v-if="!isAvailable" class="btn-unavailable">Agotado</div>
        <button
          v-else
          type="button"
          class="product-card__details-button"
          :aria-label="`Ver detalles de ${product.name}`"
          @click.stop="handleCardSelect"
        >
          Ver detalles
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.product-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;
  cursor: pointer;
}

.product-card:hover:not(.is-unavailable) {
  transform: translateY(-6px);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.08);
}

.product-card.is-unavailable {
  opacity: 0.75;
  filter: grayscale(0.85);
  cursor: default;
}

.product-card__image-container {
  position: relative;
  aspect-ratio: 4/3;
  cursor: pointer;
  background: #f1f5f9;
  overflow: hidden;
}

.product-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.product-card:hover:not(.is-unavailable) .product-card__image {
  transform: scale(1.04);
}

.product-card__badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  backdrop-filter: blur(4px);
}

.product-card__badge.promo {
  background: var(--primary, #e05a2a);
}

.product-card__badge.sold-out {
  background: #dc2626;
  right: 0.75rem;
  left: auto;
}

.product-card__quick-add {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: 999px;
  background: var(--primary, #e05a2a);
  color: #fff;
  font-weight: 800;
  font-size: 1.5rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-card__quick-add:hover {
  transform: scale(1.1) rotate(90deg);
  background: #ff5216;
}

.product-card__quick-add:active {
  transform: scale(0.95);
}

.product-card__quick-add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.4), 0 4px 12px rgba(255, 107, 53, 0.3);
}

.product-card__overlay {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Focus state for image container */
.product-card__image-container:focus-visible {
  outline: 2px solid var(--primary, #e05a2a);
  outline-offset: 2px;
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .product-card,
  .product-card__image,
  .product-card__quick-add {
    animation: none;
    transition: none;
  }
}

.product-card__content {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.product-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.product-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text, #1e293b);
  margin: 0;
  line-height: 1.3;
}

.product-card__price {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--primary, #e05a2a);
  white-space: nowrap;
}

.product-card__desc {
  font-size: 0.9rem;
  color: var(--muted, #64748b);
  margin-bottom: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  flex: 1;
}

.product-card__subcategory {
  margin: 0 0 0.35rem;
  font-size: 0.76rem;
  font-weight: 700;
  color: #334155;
}

.product-card__labels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.6rem;
}

.product-card__label-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.67rem;
  font-weight: 700;
  padding: 0.16rem 0.48rem;
}

.product-card__service-mode {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  margin-bottom: 1rem;
}

.product-card__service-mode.service-quick {
  background: rgba(22, 163, 74, 0.14);
  color: #166534;
}

.product-card__service-mode.service-prep {
  background: rgba(37, 99, 235, 0.14);
  color: #1d4ed8;
}

.product-card__footer {
  margin-top: auto;
}

.product-card__details-button {
  width: 100%;
  min-height: 44px;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  background: #ffffff;
  color: #1f2937;
  font-weight: 700;
  cursor: pointer;
}

.product-card__details-button:hover {
  background: #f8fafc;
}

.product-card__details-button:focus-visible {
  outline: 2px solid #c2410c;
  outline-offset: 2px;
}

.btn-unavailable {
  width: 100%;
  padding: 0.75rem;
  border-radius: 12px;
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  font-weight: 700;
  text-align: center;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .product-card,
  .product-card__image,
  .product-card__quick-add {
    animation: none !important;
    transition: none !important;
    transform: none !important;
  }
}
</style>
